/**
 * OnboardingWalkthrough — first-run setup wizard.
 *
 * Fires automatically on first launch (or after the user clicks "Re-run Setup"
 * in Settings → General). Walks the user through:
 *
 *   1. Welcome / overview
 *   2. Save location — confirm Documents\Journifi AI default OR pick custom.
 *      Required step (can't proceed without a writable path).
 *   3. JournifiSync — confirm sync folder OR skip if not using NinjaTrader
 *   4. Trade Grouping & Thesis — explain how sessions work and how the
 *      Coach validates trades against the stated thesis. Pure tutorial
 *      step (no settings to choose), so users land in Journal already
 *      knowing the right notation to use.
 *   5. Coach tier — show what Free includes, optional Pro license code
 *   6. Done — summary + "what's next"
 *
 * Persistence:
 *   - The data dir override goes through `window.electronAPI.storage.setDataPath`
 *     (writes to Electron's userData, not the journal data dir — chicken-and-egg).
 *   - Everything else (sync folder, license code, completion flag) goes through
 *     the standard `saveSettings()` flow once the wizard finishes.
 *
 * Props:
 *   open                   bool — controls visibility
 *   darkMode               bool
 *   currentDataInfo        { resolved, override, default, isOverride, legacyExists, legacyPath }
 *   currentSyncFolder      string — current journifi sync folder (if any)
 *   currentLicense         { tier, identifier, formatted, redeemedAt } | null
 *   onComplete             ({ dataDir, syncFolder, license, completedAt }) => void
 *                          Called when user finishes the wizard. Parent persists.
 *   onDismiss              () => void
 *                          Called if user closes mid-flow (X button). Wizard
 *                          re-opens on next launch.
 */

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, ChevronLeft, ChevronRight, Sparkles, FolderOpen, HardDrive, Check,
  AlertCircle, Loader2, Activity, ShieldCheck, KeyRound, Rocket, ArrowRight,
  Database, Layers, Target, MessageSquare, Cloud, CloudOff
} from 'lucide-react';
import { validateLicenseCode } from '../../utils/licenseCode';

// Bytes → human-readable. Mirrors electron/sync/dataSources.cjs#formatBytes
// so candidate cards display the same numbers as the main process logs.
const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const DEFAULT_SYNC_FOLDER = 'C:\\NinjaTrader8Data\\JournifiSync';

// ────────────────────────────────────────────────────────────────────────────
// Step header — shared across every step
// ────────────────────────────────────────────────────────────────────────────

const StepHeader = ({ step, total, title, subtitle, darkMode }) => (
  <div className="mb-5">
    <div className={`text-[11px] uppercase tracking-wider font-semibold mb-1 ${darkMode ? 'text-purple-300/80' : 'text-purple-700/80'}`}>
      Step {step} of {total}
    </div>
    <h2 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {subtitle}
      </p>
    )}
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// FeatureRow — copied pattern from CoachAboutModal so the wizard feels native
// ────────────────────────────────────────────────────────────────────────────

const FeatureRow = ({ icon: Icon, label, detail, darkMode }) => (
  <li className={`flex gap-3 p-3 rounded-lg border ${
    darkMode
      ? 'bg-white/[0.03] border-white/10'
      : 'bg-gray-50 border-gray-200'
  }`}>
    <div className="shrink-0">
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
        darkMode ? 'bg-purple-500/10 text-purple-300' : 'bg-purple-100 text-purple-600'
      }`}>
        <Icon size={16} />
      </div>
    </div>
    <div className="min-w-0 flex-1">
      <p className={`text-sm font-medium mb-0.5 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {label}
      </p>
      <p className={`text-xs leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {detail}
      </p>
    </div>
  </li>
);

// ────────────────────────────────────────────────────────────────────────────
// Path probe state — used by Step 2 (storage)
// ────────────────────────────────────────────────────────────────────────────

const usePathProbe = (candidatePath) => {
  const [state, setState] = useState({ status: 'idle', error: null });

  useEffect(() => {
    if (!candidatePath) {
      setState({ status: 'idle', error: null });
      return;
    }
    let cancelled = false;
    setState({ status: 'probing', error: null });
    const api = window.electronAPI?.storage;
    if (!api?.probe) {
      setState({ status: 'error', error: 'Storage API unavailable (not running in Electron)' });
      return;
    }
    api.probe(candidatePath).then((res) => {
      if (cancelled) return;
      setState(res?.ok
        ? { status: 'ok', error: null }
        : { status: 'error', error: res?.error || 'Path is not writable' });
    });
    return () => { cancelled = true; };
  }, [candidatePath]);

  return state;
};

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

const OnboardingWalkthrough = ({
  open,
  darkMode = true,
  currentDataInfo,
  currentSyncFolder,
  currentLicense,
  onComplete,
  onDismiss
}) => {
  const totalSteps = 6;
  const [step, setStep] = useState(1);

  // Step 2 — storage
  const [chosenDataDir, setChosenDataDir] = useState(
    currentDataInfo?.resolved || currentDataInfo?.default || ''
  );
  const dataDirProbe = usePathProbe(chosenDataDir);

  // Step 3 — JournifiSync
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [chosenSyncFolder, setChosenSyncFolder] = useState(
    currentSyncFolder || DEFAULT_SYNC_FOLDER
  );

  // Step 4 — License
  const [licenseInput, setLicenseInput] = useState('');
  const [licenseStatus, setLicenseStatus] = useState(null); // { kind, message }
  const [licenseChecking, setLicenseChecking] = useState(false);
  const [redeemedLicense, setRedeemedLicense] = useState(currentLicense || null);

  // Step 2 — optional merge from another existing folder (legacy F:, OneDrive
  // copy, etc.). Defaults ON when ANY existing source has data AND the user
  // is headed somewhere new — common first-run case "bring my trades over".
  const [mergeFromLegacy, setMergeFromLegacy] = useState(false);
  // Migration runtime state (populated during handleFinish)
  const [migrationState, setMigrationState] = useState({ status: 'idle', summary: null, error: null });

  // ── Data source discovery (Phase B Week 1 fix for the v7.4.2 bug) ────
  // Pre-fix the wizard only knew about ONE legacy path (the hardcoded F:
  // constant). A user with richer data in OneDrive Documents would have
  // it ignored — onboarding seeded from F: (stale), the app pointed at
  // the new folder, and months of trades vanished from the UI.
  // listSources() returns every candidate folder ranked by richness so
  // we can default to the right source and surface the others.
  const [dataSources, setDataSources] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const api = window.electronAPI?.storage;
    if (!api?.listSources) return;
    api.listSources().then(r => {
      if (cancelled) return;
      setDataSources(Array.isArray(r?.sources) ? r.sources : []);
    }).catch(() => { /* discovery failure is non-fatal — fall back to legacyPath */ });
    return () => { cancelled = true; };
  }, [open]);

  // The richest source that ISN'T already the chosen save location.
  // If one exists, the wizard offers "Use this folder" / "Merge data from
  // this folder" affordances. Excluding the current chosen path keeps the
  // copy honest ("we found ANOTHER folder with data" rather than pointing
  // at the one already selected).
  const richestOtherSource = useMemo(() => {
    return dataSources.find(s => s.exists && s.files?.['trades.json'] && s.path !== chosenDataDir) || null;
  }, [dataSources, chosenDataDir]);

  // Cloud-folder warning for the currently-chosen save location. Renderer
  // mirrors the main-process detection result via dataSources[i].cloud
  // (the candidate matching chosenDataDir) — falls back to scanning all
  // sources if no exact match (e.g. user just typed a custom path).
  const chosenCloudInfo = useMemo(() => {
    const match = dataSources.find(s => s.path === chosenDataDir);
    return match?.cloud || null;
  }, [dataSources, chosenDataDir]);

  // Re-sync local state when props change (e.g. modal re-opens with fresh data)
  useEffect(() => {
    if (open) {
      setStep(1);
      setChosenDataDir(currentDataInfo?.resolved || currentDataInfo?.default || '');
      setChosenSyncFolder(currentSyncFolder || DEFAULT_SYNC_FOLDER);
      setRedeemedLicense(currentLicense || null);
      setLicenseStatus(null);
      setLicenseInput('');
      // Default the merge toggle ON when SOMETHING richer than the chosen
      // path exists (covers F: legacy, OneDrive copy, and any other found
      // source). The actual source path is computed via richestOtherSource
      // and falls back to legacyPath if listSources didn't load.
      setMergeFromLegacy(!!(currentDataInfo?.legacyExists));
      setMigrationState({ status: 'idle', summary: null, error: null });
    }
  }, [open, currentDataInfo, currentSyncFolder, currentLicense]);

  // Close on Escape (only on welcome/done — mid-flow Escape is too easy to hit)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && (step === 1 || step === totalSteps)) {
        onDismiss?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, step, onDismiss]);

  if (!open || typeof document === 'undefined') return null;

  // ── Step actions ──────────────────────────────────────────────────────────

  const handlePickDataFolder = async () => {
    const api = window.electronAPI?.storage;
    if (!api?.pickDirectory) return;
    const res = await api.pickDirectory({
      title: 'Choose where Journifi saves your trades',
      defaultPath: chosenDataDir || currentDataInfo?.default
    });
    if (!res?.canceled && res?.path) {
      setChosenDataDir(res.path);
    }
  };

  const handlePickSyncFolder = async () => {
    const api = window.electronAPI?.storage;
    if (!api?.pickDirectory) return;
    const res = await api.pickDirectory({
      title: 'Choose your NinjaTrader sync folder',
      defaultPath: chosenSyncFolder || DEFAULT_SYNC_FOLDER
    });
    if (!res?.canceled && res?.path) {
      setChosenSyncFolder(res.path);
    }
  };

  const handleUseLegacyPath = () => {
    if (currentDataInfo?.legacyPath) {
      setChosenDataDir(currentDataInfo.legacyPath);
    }
  };

  // Snap chosenDataDir to the richest discovered source. This is the
  // recommended path when listSources surfaces a folder with more data
  // than the platform default — typically the OneDrive-rooted Documents
  // when Windows redirects Documents into OneDrive.
  const handleUseRichestSource = () => {
    if (richestOtherSource?.path) {
      setChosenDataDir(richestOtherSource.path);
    }
  };

  const handleRedeemLicense = async () => {
    const code = licenseInput.trim();
    if (!code) {
      setLicenseStatus({ kind: 'error', message: 'Enter a license code first' });
      return;
    }
    setLicenseChecking(true);
    setLicenseStatus(null);
    try {
      const result = validateLicenseCode(code);
      if (result?.valid) {
        const license = {
          tier: result.tier || 'pro',
          identifier: result.identifier || code,
          formatted: result.formatted || code,
          redeemedAt: new Date().toISOString()
        };
        setRedeemedLicense(license);
        setLicenseStatus({ kind: 'success', message: 'Pro unlocked!' });
      } else {
        setLicenseStatus({ kind: 'error', message: result?.reason || 'Invalid code' });
      }
    } catch (e) {
      setLicenseStatus({ kind: 'error', message: e.message || 'Could not validate code' });
    } finally {
      setLicenseChecking(false);
    }
  };

  const handleFinish = async () => {
    const api = window.electronAPI?.storage;
    let committedDataDir = chosenDataDir;
    let migrationSummary = null;

    // 1) Run the merge BEFORE committing the path. If migration fails, we
    //    want the user to fall back to inspecting the source folder, not be
    //    left pointing the app at an empty new folder.
    //
    // Migration source preference (Phase B Week 1 fix):
    //   1. The richest OTHER source surfaced by listSources (most likely
    //      the OneDrive copy when Documents redirects, or the F: legacy)
    //   2. Fall back to legacyPath only when listSources hasn't loaded
    //      or has no candidates other than chosenDataDir
    // This is the v7.4.3 fix for the original bug: previously the wizard
    // ONLY knew about legacyPath and silently migrated stale F: data over
    // the user's real OneDrive data.
    const migrationSource = (richestOtherSource?.path)
      || (currentDataInfo?.legacyPath && currentDataInfo.legacyPath !== chosenDataDir
            ? currentDataInfo.legacyPath
            : null);
    if (mergeFromLegacy && migrationSource && migrationSource !== chosenDataDir) {
      if (api?.migrate) {
        setMigrationState({ status: 'running', summary: null, error: null });
        const result = await api.migrate({
          from: migrationSource,
          to: chosenDataDir
        });
        if (!result?.success) {
          setMigrationState({ status: 'error', summary: null, error: result?.error || 'Migration failed' });
          setStep(2);
          return;
        }
        migrationSummary = result.summary;
        setMigrationState({ status: 'done', summary: result.summary, error: null });
      }
    }

    // 2) Commit the data path AFTER the merge so the next load-trades /
    //    save-settings call lands at the new path with the merged data
    //    already present.
    if (api?.setDataPath && chosenDataDir) {
      const res = await api.setDataPath(chosenDataDir);
      if (!res?.success) {
        setStep(2);
        return;
      }
      committedDataDir = res.path;
    }

    onComplete?.({
      dataDir: committedDataDir,
      syncFolder: syncEnabled ? chosenSyncFolder : null,
      license: redeemedLicense,
      completedAt: new Date().toISOString(),
      migrationSummary
    });
  };

  // ── Step content ──────────────────────────────────────────────────────────

  const renderStep = () => {
    if (step === 1) return renderWelcome();
    if (step === 2) return renderStorage();
    if (step === 3) return renderSync();
    if (step === 4) return renderGrouping();
    if (step === 5) return renderTier();
    if (step === 6) return renderDone();
    return null;
  };

  function renderWelcome() {
    return (
      <div>
        <StepHeader
          step={1}
          total={totalSteps}
          title="Welcome to Journifi AI"
          subtitle="A few quick choices and you'll be ready to journal. This takes about 90 seconds."
          darkMode={darkMode}
        />

        <ul className="space-y-2 mb-6">
          <FeatureRow
            darkMode={darkMode}
            icon={HardDrive}
            label="You choose where your data lives"
            detail="Trades, settings, and screenshots stay on YOUR machine in a folder you pick. No cloud, no telemetry."
          />
          <FeatureRow
            darkMode={darkMode}
            icon={Activity}
            label="Real-time sync from NinjaTrader (optional)"
            detail="If you trade on NT8, executions stream into the journal as they fill. We'll set the sync folder in a moment."
          />
          <FeatureRow
            darkMode={darkMode}
            icon={Sparkles}
            label="AI Coach with day grades + 30-day trends"
            detail="Free includes day grades, mistake clusters, news auto-tagging. Pro adds mid-trade event whispers and historical baselines."
          />
        </ul>

        <p className={`text-xs leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          You can re-run this setup any time from <span className={darkMode ? 'text-gray-200' : 'text-gray-700'}>Settings → General → Re-run Setup</span>.
        </p>
      </div>
    );
  }

  function renderStorage() {
    const isLegacy = currentDataInfo?.legacyExists
      && chosenDataDir !== currentDataInfo.legacyPath
      && currentDataInfo.legacyPath;

    return (
      <div>
        <StepHeader
          step={2}
          total={totalSteps}
          title="Where should we save your data?"
          subtitle="Trades, settings, and screenshots all live in one folder. Pick somewhere you'll find later — Documents is the safe default."
          darkMode={darkMode}
        />

        <div className={`p-3 rounded-lg border mb-3 ${
          darkMode
            ? 'bg-white/[0.03] border-white/10'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <FolderOpen size={16} className={darkMode ? 'text-purple-300' : 'text-purple-600'} />
            <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
              Save location
            </span>
          </div>
          <div className={`font-mono text-sm break-all p-2 rounded border ${
            darkMode
              ? 'bg-black/30 border-white/10 text-gray-100'
              : 'bg-white border-gray-200 text-gray-800'
          }`}>
            {chosenDataDir || '(none selected)'}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button
              type="button"
              onClick={handlePickDataFolder}
              className={`text-xs px-3 py-1.5 rounded-md border font-medium transition-colors ${
                darkMode
                  ? 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                  : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'
              }`}
            >
              Choose folder…
            </button>
            {currentDataInfo?.default && chosenDataDir !== currentDataInfo.default && (
              <button
                type="button"
                onClick={() => setChosenDataDir(currentDataInfo.default)}
                className={`text-xs px-3 py-1.5 rounded-md border font-medium ${
                  darkMode
                    ? 'bg-transparent border-white/10 text-gray-300 hover:bg-white/5'
                    : 'bg-transparent border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Use default
              </button>
            )}
            {isLegacy && (
              <button
                type="button"
                onClick={handleUseLegacyPath}
                className={`text-xs px-3 py-1.5 rounded-md border font-medium ${
                  darkMode
                    ? 'bg-amber-500/10 border-amber-400/30 text-amber-200 hover:bg-amber-500/20'
                    : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                }`}
                title={currentDataInfo.legacyPath}
              >
                Keep existing data ({currentDataInfo.legacyPath.split('\\').pop()})
              </button>
            )}
          </div>

          {/* Cloud-folder warning (Phase B Week 1) — when the user's chosen
              save location lives inside OneDrive/Dropbox/iCloud/etc.
              Multi-device + cloud sync = silent data loss (cloud can't merge
              JSON, picks a winner, renames the loser). Surfaced live so the
              user sees the risk BEFORE committing. */}
          {chosenCloudInfo && (
            <div className={`mt-3 p-2.5 rounded-md border text-xs flex items-start gap-2 ${
              darkMode ? 'bg-red-500/10 border-red-400/40 text-red-200' : 'bg-red-50 border-red-300 text-red-800'
            }`}>
              <Cloud size={14} className={`mt-0.5 shrink-0 ${darkMode ? 'text-red-300' : 'text-red-600'}`} />
              <div className="leading-snug">
                <strong>{chosenCloudInfo.provider}</strong> can&apos;t merge JSON. If you ever run Journifi on a second PC,
                this folder will silently drop trades when the cloud picks a &quot;winner&quot; between two saves.
                Pick a local (non-cloud) folder for safety.
              </div>
            </div>
          )}

          {/* Probe result */}
          <div className="mt-3 min-h-[20px]">
            {dataDirProbe.status === 'probing' && (
              <div className={`flex items-center gap-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <Loader2 size={12} className="animate-spin" />
                Checking write access…
              </div>
            )}
            {dataDirProbe.status === 'ok' && (
              <div className={`flex items-center gap-2 text-xs ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                <Check size={12} />
                Folder is writable. We’ll create it if it doesn’t exist yet.
              </div>
            )}
            {dataDirProbe.status === 'error' && (
              <div className={`flex items-start gap-2 text-xs ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                <AlertCircle size={12} className="mt-0.5 shrink-0" />
                <span>
                  Can’t write here: {dataDirProbe.error}.
                  {' '}Try a folder under your user profile (Documents, Desktop, or any drive you own).
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Bring existing data over (Phase B Week 1 — generalized from "legacy
            only" to "richest other source"). Source preference:
              1. richestOtherSource — the discovery output picks whichever
                 existing folder (OneDrive, F: legacy, custom path) has
                 the most trade data
              2. Fallback to currentDataInfo.legacyPath when listSources
                 hasn't loaded yet
            Hidden when the chosen save location IS the richest source
            (merge would be a no-op). */}
        {(() => {
          const source = richestOtherSource
            || ((currentDataInfo?.legacyExists && currentDataInfo?.legacyPath !== chosenDataDir)
                  ? {
                      path: currentDataInfo.legacyPath,
                      label: 'Legacy folder',
                      files: {},
                      cloud: null,
                    }
                  : null);
          if (!source || !chosenDataDir || source.path === chosenDataDir) return null;
          const tradesInfo = source.files?.['trades.json'];
          const tradesSize = tradesInfo ? formatBytes(tradesInfo.size) : null;
          const tradesDate = tradesInfo?.modifiedIso ? tradesInfo.modifiedIso.slice(0, 10) : null;
          return (
            <div className={`mb-3 p-3 rounded-lg border ${
              mergeFromLegacy
                ? (darkMode
                    ? 'bg-purple-500/10 border-purple-400/30'
                    : 'bg-purple-50 border-purple-200')
                : (darkMode
                    ? 'bg-white/[0.02] border-white/10'
                    : 'bg-gray-50 border-gray-200')
            }`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mergeFromLegacy}
                  onChange={(e) => setMergeFromLegacy(e.target.checked)}
                  className="mt-0.5 shrink-0 w-4 h-4 accent-purple-500 cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <Database size={14} className={darkMode ? 'text-purple-300' : 'text-purple-600'} />
                    <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Bring data over from {source.label || 'this folder'}
                    </span>
                    {tradesSize && (
                      <span className={`text-[10px] uppercase tracking-wide ${darkMode ? 'text-purple-300/70' : 'text-purple-700/70'}`}>
                        {tradesSize}{tradesDate ? ` · ${tradesDate}` : ''}
                      </span>
                    )}
                    {source.cloud && (
                      <span className={`text-[10px] uppercase tracking-wide ${darkMode ? 'text-amber-300/80' : 'text-amber-700/80'}`}>
                        in {source.cloud.provider}
                      </span>
                    )}
                  </div>
                  <div className={`flex items-center gap-1.5 text-xs font-mono mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span className="truncate" title={source.path}>{source.path.split('\\').slice(-2).join('\\')}</span>
                    <ArrowRight size={11} className="shrink-0" />
                    <span className="truncate" title={chosenDataDir}>{chosenDataDir.split('\\').slice(-2).join('\\')}</span>
                  </div>
                  <p className={`text-xs leading-snug ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Copies trades, settings, screenshots, and ATM template versions over.
                    {' '}<span className={darkMode ? 'text-gray-200' : 'text-gray-700'}>Non-destructive</span> — original files stay put. If both folders have the same trade, the newer edit wins.
                  </p>
                  {richestOtherSource && richestOtherSource.path !== chosenDataDir && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); handleUseRichestSource(); }}
                      className={`mt-2 text-[11px] px-2 py-1 rounded border font-medium ${
                        darkMode
                          ? 'bg-emerald-500/10 border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/20'
                          : 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                      }`}
                    >
                      Use this folder as save location instead
                    </button>
                  )}
                </div>
              </label>
            </div>
          );
        })()}

        {/* Migration error — only renders if a previous Finish attempt failed */}
        {migrationState.status === 'error' && (
          <div className={`mb-3 p-3 rounded-lg border ${
            darkMode ? 'bg-red-500/10 border-red-400/30' : 'bg-red-50 border-red-200'
          }`}>
            <div className={`flex items-start gap-2 text-xs ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold mb-0.5">Couldn’t merge from old folder</p>
                <p>{migrationState.error}</p>
              </div>
            </div>
          </div>
        )}

        <div className={`text-xs leading-relaxed p-3 rounded-md ${
          darkMode ? 'bg-white/[0.02] text-gray-400' : 'bg-gray-50 text-gray-500'
        }`}>
          <p className="mb-1"><strong className={darkMode ? 'text-gray-200' : 'text-gray-700'}>Tips:</strong></p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Avoid <code>Program Files</code> and the C:\ root — they need admin to write.</li>
            <li>
              <span className="inline-flex items-center gap-1 align-middle">
                <CloudOff size={11} className={darkMode ? 'text-amber-300' : 'text-amber-600'} />
                <span><strong className={darkMode ? 'text-amber-200' : 'text-amber-700'}>Avoid OneDrive / Dropbox / iCloud folders</strong> for multi-PC use</span>
              </span>
              {' '}— cloud sync can&apos;t merge JSON, so concurrent writes from two devices silently drop trades. Multi-device sync is on the roadmap as a separate, safe feature.
            </li>
            <li>You can move the folder later from Settings → Storage, and merge in old data with the &quot;Merge from another folder&quot; button.</li>
          </ul>
        </div>
      </div>
    );
  }

  function renderSync() {
    return (
      <div>
        <StepHeader
          step={3}
          total={totalSteps}
          title="NinjaTrader sync"
          subtitle="If you trade on NinjaTrader 8, the JournifiSync AddOn streams executions into the journal in real time. Skip this if you're not using NT8."
          darkMode={darkMode}
        />

        <div className="space-y-3 mb-4">
          <button
            type="button"
            onClick={() => setSyncEnabled(true)}
            className={`w-full text-left p-3 rounded-lg border transition-colors ${
              syncEnabled
                ? (darkMode
                    ? 'bg-purple-500/10 border-purple-400/40'
                    : 'bg-purple-50 border-purple-300')
                : (darkMode
                    ? 'bg-white/[0.02] border-white/10 hover:bg-white/[0.04]'
                    : 'bg-gray-50 border-gray-200 hover:bg-white')
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                syncEnabled
                  ? (darkMode ? 'border-purple-400 bg-purple-500/20' : 'border-purple-500 bg-purple-100')
                  : (darkMode ? 'border-white/20' : 'border-gray-300')
              }`}>
                {syncEnabled && <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-purple-300' : 'bg-purple-600'}`} />}
              </div>
              <div className="flex-1">
                <div className={`font-medium text-sm mb-0.5 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  I use NinjaTrader 8 — set up sync
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  We’ll watch this folder for execution files. The default is where the AddOn writes.
                </div>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setSyncEnabled(false)}
            className={`w-full text-left p-3 rounded-lg border transition-colors ${
              !syncEnabled
                ? (darkMode
                    ? 'bg-purple-500/10 border-purple-400/40'
                    : 'bg-purple-50 border-purple-300')
                : (darkMode
                    ? 'bg-white/[0.02] border-white/10 hover:bg-white/[0.04]'
                    : 'bg-gray-50 border-gray-200 hover:bg-white')
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                !syncEnabled
                  ? (darkMode ? 'border-purple-400 bg-purple-500/20' : 'border-purple-500 bg-purple-100')
                  : (darkMode ? 'border-white/20' : 'border-gray-300')
              }`}>
                {!syncEnabled && <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-purple-300' : 'bg-purple-600'}`} />}
              </div>
              <div className="flex-1">
                <div className={`font-medium text-sm mb-0.5 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Skip — I’ll add trades manually (or set this up later)
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  You can configure sync any time from Settings → JournifiSync.
                </div>
              </div>
            </div>
          </button>
        </div>

        {syncEnabled && (
          <div className={`p-3 rounded-lg border ${
            darkMode
              ? 'bg-white/[0.03] border-white/10'
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen size={16} className={darkMode ? 'text-purple-300' : 'text-purple-600'} />
              <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                Sync folder
              </span>
            </div>
            <div className={`font-mono text-sm break-all p-2 rounded border ${
              darkMode
                ? 'bg-black/30 border-white/10 text-gray-100'
                : 'bg-white border-gray-200 text-gray-800'
            }`}>
              {chosenSyncFolder}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={handlePickSyncFolder}
                className={`text-xs px-3 py-1.5 rounded-md border font-medium ${
                  darkMode
                    ? 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                    : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'
                }`}
              >
                Choose folder…
              </button>
              {chosenSyncFolder !== DEFAULT_SYNC_FOLDER && (
                <button
                  type="button"
                  onClick={() => setChosenSyncFolder(DEFAULT_SYNC_FOLDER)}
                  className={`text-xs px-3 py-1.5 rounded-md border font-medium ${
                    darkMode
                      ? 'bg-transparent border-white/10 text-gray-300 hover:bg-white/5'
                      : 'bg-transparent border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Reset to NT default
                </button>
              )}
            </div>
            <p className={`text-xs mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              The JournifiSync AddOn writes here. If you haven’t installed it yet, you can do that after setup —
              the journal will work fine and start syncing as soon as the AddOn is loaded in NT8.
            </p>
          </div>
        )}
      </div>
    );
  }

  function renderGrouping() {
    // Pure tutorial step — no settings to choose. Shows the user how trade
    // grouping + thesis works before they land in Journal, so the right
    // notation patterns are top-of-mind from day one. This matters because
    // the thesisValidation analyzer reads the thesis text and checks
    // direction/symbol drift — vague theses produce zero signal.
    const goodExamples = [
      'Long MNQ on first touch of 15M 09 EMA — runner above prior swing',
      'Fade ES into supply at HOD if VWAP rejects — short with structure',
      'Buy MNQ dip into 1H value low; skip if CPI moves it first',
    ];
    const avoidExamples = [
      'feeling good today',
      'watch the chart',
      'long',
    ];

    return (
      <div>
        <StepHeader
          step={4}
          total={totalSteps}
          title="Trade Grouping & Thesis"
          subtitle="Group related trades into a session, write a one-line thesis up front, and the Coach grades execution against the plan you stated. The discipline layer of the journal."
          darkMode={darkMode}
        />

        {/* What is a session — the mental model in one paragraph */}
        <div className={`p-3 rounded-lg border mb-3 ${
          darkMode ? 'bg-purple-500/[0.06] border-purple-400/20' : 'bg-purple-50 border-purple-200'
        }`}>
          <div className="flex items-center gap-2 mb-1.5">
            <Layers size={16} className={darkMode ? 'text-purple-300' : 'text-purple-600'} />
            <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              What is a Trade Group?
            </span>
          </div>
          <p className={`text-xs leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            A group bundles trades that share intent — same setup, same window, same plan.
            On the Journal tab, expand a day, hit <span className="font-medium">Group these trades</span> and
            give the group a name + thesis. Multiple groups per day are fine; one trade can only belong to one.
          </p>
        </div>

        {/* Good thesis examples */}
        <div className={`p-3 rounded-lg border mb-3 ${
          darkMode ? 'bg-green-500/[0.05] border-green-500/20' : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Check size={16} className={darkMode ? 'text-green-400' : 'text-green-600'} />
            <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Good thesis notation
            </span>
          </div>
          <ul className="space-y-1.5">
            {goodExamples.map((ex, i) => (
              <li key={i} className={`text-xs leading-snug font-mono ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                <span className={`mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>→</span>
                {ex}
              </li>
            ))}
          </ul>
          <div className={`mt-2.5 pt-2 border-t text-[11px] leading-relaxed ${
            darkMode ? 'border-green-500/20 text-gray-400' : 'border-green-200 text-gray-500'
          }`}>
            Each one names <span className="font-medium">direction</span> (long/fade/buy),
            the <span className="font-medium">symbol</span> (MNQ, ES), and a
            <span className="font-medium"> structural level</span> (15M 09 EMA, supply at HOD).
            That gives the Coach enough to grade the executed trades against the stated plan.
          </div>
        </div>

        {/* Avoid */}
        <div className={`p-3 rounded-lg border mb-3 ${
          darkMode ? 'bg-amber-500/[0.05] border-amber-500/20' : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={16} className={darkMode ? 'text-amber-400' : 'text-amber-600'} />
            <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Too vague — Coach stays silent
            </span>
          </div>
          <ul className="space-y-1.5">
            {avoidExamples.map((ex, i) => (
              <li key={i} className={`text-xs leading-snug font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <span className={`mr-2 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>×</span>
                {ex}
              </li>
            ))}
          </ul>
          <div className={`mt-2.5 pt-2 border-t text-[11px] leading-relaxed ${
            darkMode ? 'border-amber-500/20 text-gray-400' : 'border-amber-200 text-gray-500'
          }`}>
            Theses under 12 characters or with no direction word are skipped — the Coach
            won&apos;t grade what you didn&apos;t commit to. Either write the plan or rewrite it
            mid-session if conditions change.
          </div>
        </div>

        {/* What the Coach watches for */}
        <ul className="space-y-2">
          <FeatureRow
            darkMode={darkMode}
            icon={Target}
            label="Direction discipline"
            detail="If your thesis says long and you took shorts, the Coach flags the drift. If both directions appear in the thesis, the check is skipped — own the ambiguity."
          />
          <FeatureRow
            darkMode={darkMode}
            icon={MessageSquare}
            label="Symbol discipline"
            detail="If your thesis names MNQ but trades landed on ES, that's a flag. Indicator names like EMA / VWAP / FVG / FOMC aren't mistaken for tickers — only real symbols count."
          />
          <FeatureRow
            darkMode={darkMode}
            icon={Sparkles}
            label="Aligned-success badge"
            detail="When 3+ trades all stay on thesis, the Coach surfaces a positive observation. Discipline as a repeatable signal — note the conditions that produced it."
          />
        </ul>
      </div>
    );
  }

  function renderTier() {
    const isPro = redeemedLicense?.tier === 'pro';
    return (
      <div>
        <StepHeader
          step={5}
          total={totalSteps}
          title="AI Coach"
          subtitle="The AI Coach is free for everyone. If you have a Pro license code from the closed beta, you can redeem it now — or skip and add it later from Settings → License."
          darkMode={darkMode}
        />

        <div className={`p-3 rounded-lg border mb-3 ${
          isPro
            ? (darkMode ? 'bg-purple-500/10 border-purple-400/30' : 'bg-purple-50 border-purple-200')
            : (darkMode ? 'bg-green-500/[0.04] border-green-500/20' : 'bg-green-50/70 border-green-200')
        }`}>
          <div className="flex items-center gap-2 mb-1">
            {isPro ? (
              <>
                <Sparkles size={16} className={darkMode ? 'text-purple-300' : 'text-purple-600'} />
                <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Pro unlocked
                </span>
              </>
            ) : (
              <>
                <Check size={16} className={darkMode ? 'text-green-400' : 'text-green-600'} />
                <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Free tier — ready to go
                </span>
              </>
            )}
          </div>
          <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {isPro
              ? `Code ${redeemedLicense.formatted || redeemedLicense.identifier} accepted. Mid-trade event whispers and historical baselines are unlocked.`
              : 'You\'ll get day grades, mistake clusters, time-of-day windows, news auto-tagging, and 30-day discipline trends.'}
          </p>
        </div>

        {!isPro && (
          <div className={`p-3 rounded-lg border ${
            darkMode
              ? 'bg-white/[0.03] border-white/10'
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <KeyRound size={14} className={darkMode ? 'text-purple-300' : 'text-purple-600'} />
              <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                Have a Pro license code?
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={licenseInput}
                onChange={(e) => setLicenseInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRedeemLicense(); }}
                placeholder="JRNFI-XXXX-XXXX-XXXX"
                className="flex-1 input-modern font-mono"
              />
              <button
                type="button"
                onClick={handleRedeemLicense}
                disabled={licenseChecking || !licenseInput.trim()}
                className={`text-sm px-4 py-2 rounded-md font-medium transition-colors ${
                  licenseChecking || !licenseInput.trim()
                    ? (darkMode ? 'bg-white/5 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                    : (darkMode
                        ? 'bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 border border-purple-400/30'
                        : 'bg-purple-100 text-purple-800 hover:bg-purple-200 border border-purple-300')
                }`}
              >
                {licenseChecking ? <Loader2 size={14} className="animate-spin" /> : 'Redeem'}
              </button>
            </div>
            {licenseStatus && (
              <div className={`mt-2 flex items-center gap-2 text-xs ${
                licenseStatus.kind === 'success'
                  ? (darkMode ? 'text-green-400' : 'text-green-700')
                  : (darkMode ? 'text-red-300' : 'text-red-700')
              }`}>
                {licenseStatus.kind === 'success' ? <Check size={12} /> : <AlertCircle size={12} />}
                {licenseStatus.message}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderDone() {
    const isPro = redeemedLicense?.tier === 'pro';
    return (
      <div>
        <StepHeader
          step={6}
          total={totalSteps}
          title="You're set"
          subtitle="Here's where everything ended up. You can change any of this later from Settings."
          darkMode={darkMode}
        />

        <ul className="space-y-2 mb-5">
          <FeatureRow
            darkMode={darkMode}
            icon={HardDrive}
            label="Save location"
            detail={chosenDataDir}
          />
          {/* Migration outcome — only shown if user opted into merge AND it ran */}
          {migrationState.status === 'done' && migrationState.summary && !migrationState.summary.noop && (
            <FeatureRow
              darkMode={darkMode}
              icon={Database}
              label="Existing data merged"
              detail={(() => {
                const s = migrationState.summary;
                const parts = [];
                if (s.trades?.added || s.trades?.updated) {
                  parts.push(`${s.trades.added + s.trades.updated} trade${s.trades.added + s.trades.updated === 1 ? '' : 's'}`);
                }
                if (s.screenshots?.copied) {
                  parts.push(`${s.screenshots.copied} screenshot${s.screenshots.copied === 1 ? '' : 's'}`);
                }
                if (s.ntVersions?.addedKeys || s.ntVersions?.addedVersions) {
                  parts.push(`${s.ntVersions.addedVersions} ATM version${s.ntVersions.addedVersions === 1 ? '' : 's'}`);
                }
                if (s.settings?.carriedOver) {
                  parts.push(`${s.settings.carriedOver} setting${s.settings.carriedOver === 1 ? '' : 's'}`);
                }
                return parts.length
                  ? `Merged ${parts.join(', ')} from your old folder. Original files left in place.`
                  : 'No new items found in your old folder — destination already had everything.';
              })()}
            />
          )}
          <FeatureRow
            darkMode={darkMode}
            icon={Activity}
            label={syncEnabled ? 'NinjaTrader sync' : 'NinjaTrader sync (skipped)'}
            detail={syncEnabled
              ? chosenSyncFolder
              : 'You can enable sync any time from Settings → JournifiSync.'}
          />
          <FeatureRow
            darkMode={darkMode}
            icon={isPro ? Sparkles : ShieldCheck}
            label={isPro ? 'AI Coach (Pro)' : 'AI Coach (Free)'}
            detail={isPro
              ? `Code ${redeemedLicense.formatted || redeemedLicense.identifier} active.`
              : 'Day grades, mistake clusters, news auto-tagging, 30-day trends.'}
          />
        </ul>

        <div className={`p-3 rounded-lg border ${
          darkMode
            ? 'bg-purple-500/[0.06] border-purple-400/20'
            : 'bg-purple-50/60 border-purple-200'
        }`}>
          <div className="flex items-start gap-2">
            <Rocket size={16} className={`mt-0.5 shrink-0 ${darkMode ? 'text-purple-300' : 'text-purple-600'}`} />
            <div>
              <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                What’s next
              </p>
              <ul className={`text-xs space-y-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>• Add an account from Settings → General to start tracking trades.</li>
                {syncEnabled && <li>• Install the JournifiSync AddOn in NT8 (Tools → New AddOn) if you haven’t yet.</li>}
                <li>• Open the Journal tab — your live coach feed is on the right.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Footer (Back / Next / Finish) ─────────────────────────────────────────

  const canAdvance = () => {
    if (step === 2) return dataDirProbe.status === 'ok';
    return true;
  };

  const renderFooter = () => {
    const isFirst = step === 1;
    const isLast = step === totalSteps;
    return (
      <div className={`flex items-center justify-between gap-3 pt-4 border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <button
          type="button"
          onClick={() => setStep(s => Math.max(1, s - 1))}
          disabled={isFirst}
          className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-md font-medium ${
            isFirst
              ? (darkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 cursor-not-allowed')
              : (darkMode ? 'text-gray-200 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-100')
          }`}
        >
          <ChevronLeft size={14} /> Back
        </button>

        <div className={`flex items-center gap-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i + 1 === step
                  ? (darkMode ? 'bg-purple-400 w-6' : 'bg-purple-500 w-6')
                  : i + 1 < step
                    ? (darkMode ? 'bg-purple-400/40 w-1.5' : 'bg-purple-300 w-1.5')
                    : (darkMode ? 'bg-white/10 w-1.5' : 'bg-gray-200 w-1.5')
              }`}
            />
          ))}
        </div>

        {isLast ? (
          <button
            type="button"
            onClick={handleFinish}
            disabled={migrationState.status === 'running'}
            className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-md font-semibold ${
              migrationState.status === 'running'
                ? (darkMode ? 'bg-white/5 text-gray-500 cursor-wait' : 'bg-gray-100 text-gray-400 cursor-wait')
                : (darkMode
                    ? 'bg-purple-500/25 border border-purple-400/50 text-purple-50 ring-1 ring-inset ring-purple-400/25 hover:bg-purple-500/35 hover:border-purple-400/70 hover:shadow-[0_0_18px_rgba(168,85,247,0.3)] transition-[background-color,border-color,box-shadow,color] duration-150'
                    : 'bg-purple-100 border border-purple-300 text-purple-800 hover:bg-purple-200 hover:border-purple-400 transition-[background-color,border-color,box-shadow,color] duration-150')
            }`}
          >
            {migrationState.status === 'running' ? (
              <><Loader2 size={14} className="animate-spin" /> Merging your data…</>
            ) : (
              <><Rocket size={14} /> Get started</>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => canAdvance() && setStep(s => Math.min(totalSteps, s + 1))}
            disabled={!canAdvance()}
            className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-md font-medium ${
              !canAdvance()
                ? (darkMode ? 'bg-white/5 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                : (darkMode
                    ? 'bg-purple-500/25 border border-purple-400/50 text-purple-50 ring-1 ring-inset ring-purple-400/25 hover:bg-purple-500/35 hover:border-purple-400/70 hover:shadow-[0_0_18px_rgba(168,85,247,0.3)] transition-[background-color,border-color,box-shadow,color] duration-150'
                    : 'bg-purple-100 border border-purple-300 text-purple-800 hover:bg-purple-200 hover:border-purple-400 transition-[background-color,border-color,box-shadow,color] duration-150')
            }`}
          >
            Next <ChevronRight size={14} />
          </button>
        )}
      </div>
    );
  };

  // ── Modal shell ───────────────────────────────────────────────────────────

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-label="Onboarding setup">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 ${darkMode ? 'bg-black/70 backdrop-blur-sm' : 'bg-gray-900/40 backdrop-blur-sm'}`}
        onClick={(step === 1 || step === totalSteps) ? onDismiss : undefined}
      />

      {/* Card */}
      <div className={`relative w-full max-w-xl rounded-xl shadow-2xl border ${
        darkMode
          ? 'bg-gray-900 border-white/10'
          : 'bg-white border-gray-200'
      }`}>
        {/* Close button (only on welcome/done — see Escape handler) */}
        {(step === 1 || step === totalSteps) && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close setup"
            title="Close (you can re-run this from Settings)"
            className={`absolute top-3 right-3 p-1.5 rounded-md ${
              darkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <X size={16} />
          </button>
        )}

        <div className="p-6">
          {renderStep()}
          <div className="mt-6">
            {renderFooter()}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OnboardingWalkthrough;
